import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PhoneInput } from '../../components/ui/PhoneInput.jsx';
import { ComboboxSelect } from '../../components/ui/ComboboxSelect.jsx';
import { TODOS_LOS_ESTADOS } from '../estados/useEstados.js';

const ORIGENES = ['Presencial', 'Google', 'WhatsApp', 'Redes', 'Mail', 'Referido', 'Web', 'Informe'];
const ABONOS = ['Matrícula', 'Cuota', 'Matrícula + Cta', 'Matrícula Beca', 'Matrícula Beca + Cta'];

const schema = z.object({
  nombre_apellido: z.string().min(1, 'El nombre y apellido es requerido'),
  telefono: z.string().min(1, 'El teléfono es requerido'),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  dni: z.string().optional(),
  fecha_nac: z.string().optional(),
  estado: z.string().optional(),
  origen: z.enum(['Presencial', 'Google', 'WhatsApp', 'Redes', 'Mail', 'Referido', 'Web', 'Informe']).optional(),
  abono: z.enum(['Matrícula', 'Cuota', 'Matrícula + Cta', 'Matrícula Beca', 'Matrícula Beca + Cta']).optional(),
  verificado: z.boolean().optional(),
  observacion: z.string().optional(),
});

export function LeadForm({ defaultValues, onSubmit, isPending, onCancel }) {
  const { register, control, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: defaultValues ?? { verificado: false },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Campo label="Nombre y apellido *" error={errors.nombre_apellido?.message}>
          <input {...register('nombre_apellido')} className={inputCls} placeholder="García Juan Carlos" />
        </Campo>

        <Campo label="Teléfono *" error={errors.telefono?.message}>
          <Controller
            name="telefono"
            control={control}
            render={({ field }) => <PhoneInput {...field} />}
          />
        </Campo>

        <Campo label="Email" error={errors.email?.message}>
          <input {...register('email')} type="email" className={inputCls} placeholder="juan@ejemplo.com" />
        </Campo>

        <Campo label="DNI" error={errors.dni?.message}>
          <input {...register('dni')} className={inputCls} placeholder="30123456" />
        </Campo>

        <Campo label="Fecha de nacimiento" error={errors.fecha_nac?.message}>
          <input {...register('fecha_nac')} type="date" className={inputCls} />
        </Campo>

        <Campo label="Origen" error={errors.origen?.message}>
          <Controller
            name="origen"
            control={control}
            render={({ field }) => (
              <ComboboxSelect
                options={ORIGENES.map((o) => ({ value: o, label: o }))}
                value={field.value}
                onChange={field.onChange}
                placeholder="Seleccionar origen..."
              />
            )}
          />
        </Campo>

        <Campo label="Estado" error={errors.estado?.message}>
          <Controller
            name="estado"
            control={control}
            render={({ field }) => (
              <ComboboxSelect
                options={TODOS_LOS_ESTADOS.map((e) => ({ value: e, label: e }))}
                value={field.value}
                onChange={field.onChange}
                placeholder="Nuevo (por defecto)"
              />
            )}
          />
        </Campo>

        <Campo label="Abono" error={errors.abono?.message}>
          <Controller
            name="abono"
            control={control}
            render={({ field }) => (
              <ComboboxSelect
                options={ABONOS.map((a) => ({ value: a, label: a }))}
                value={field.value}
                onChange={field.onChange}
                placeholder="Seleccionar abono..."
              />
            )}
          />
        </Campo>

      </div>

      <Campo label="Observación" error={errors.observacion?.message}>
        <textarea
          {...register('observacion')}
          rows={3}
          className={inputCls}
          placeholder="Notas sobre el lead..."
        />
      </Campo>

      <label className="flex items-center gap-2 cursor-pointer">
        <input {...register('verificado')} type="checkbox" className="rounded" />
        <span className="font-['Inter'] text-sm text-[#6B7280]">Verificado</span>
      </label>

      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={isPending}
          className="flex-1 bg-[#1E9BE8] hover:bg-[#1780C5] text-white font-['Raleway'] font-bold text-sm px-6 py-3 rounded-md transition-colors min-h-[44px] disabled:opacity-50"
        >
          {isPending ? 'Guardando...' : 'Guardar'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 border-2 border-[#0D2B5C] text-[#0D2B5C] hover:bg-[#0D2B5C] hover:text-white font-['Raleway'] font-bold text-sm px-6 py-3 rounded-md transition-colors min-h-[44px]"
          >
            Cancelar
          </button>
        )}
      </div>
    </form>
  );
}

function Campo({ label, error, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="font-['Raleway'] text-[#1E9BE8] text-xs font-semibold tracking-widest uppercase">
        {label}
      </label>
      {children}
      {error && <span className="text-red-500 text-xs font-['Inter']">{error}</span>}
    </div>
  );
}

const inputCls = "border border-gray-200 rounded-md px-3 py-2 text-sm font-['Inter'] focus:outline-none focus:ring-2 focus:ring-[#0D2B5C] w-full";
